/**
 * Задание stylepack/unit-picker-look (слово владельца 21.10.2026: повторять
 * чужую воронку в точности). Вид выбора роста и веса: крупная цифра по
 * центру без рамки с мелкой единицей рядом и переключатель единиц «пилюлей».
 *
 * ЗЕРКАЛО канона сервера `backend/src/ai-funnels/block-schema/unit-picker-look.ts`:
 * перечни ниже читает с диска сторож `unit-picker-look.ends.spec.ts` и сверяет
 * со схемой, типами редактора и панелью в обе стороны — форму `[...] as const`
 * и `{ min: N, max: M } as const` не менять, не обновив его.
 *
 * Нет полей — вид прежний (поле в рамке, две кнопки справа).
 */

export const UNIT_PICKER_LOOKS = ['field', 'big-number'] as const;
export const UNIT_TOGGLE_LOOKS = ['buttons', 'pill'] as const;
export const UNIT_TOGGLE_POSITIONS = ['right', 'above', 'below'] as const;
export const UNIT_PICKER_VALUE_FONT_SIZE_BOUNDS = { min: 24, max: 96 } as const;
/** Задание stylepack/unit-toggle-case: регистр и кегль подписи переключателя. */
export const UNIT_TOGGLE_TEXT_TRANSFORMS = ['none', 'uppercase'] as const;
export const UNIT_TOGGLE_FONT_SIZE_BOUNDS = { min: 10, max: 24 } as const;
export const UNIT_PICKER_COLOR_FIELDS = [
  'unitPickerValueColor',
  'unitPickerUnderlineColor',
  'unitToggleActiveBg',
  'unitToggleActiveText',
  'unitToggleBg',
  'unitToggleText',
] as const;

/**
 * Задание stylepack/unit-set: набор единиц `unitOptions` — обе (как раньше)
 * или одна. Зеркало `WEIGHT_UNIT_OPTIONS`/`HEIGHT_UNIT_OPTIONS` сервера.
 */
export const WEIGHT_UNIT_OPTIONS = ['both', 'kg', 'lb'] as const;
export const HEIGHT_UNIT_OPTIONS = ['both', 'cm', 'ft'] as const;

export type WeightUnitOption = (typeof WEIGHT_UNIT_OPTIONS)[number];
export type HeightUnitOption = (typeof HEIGHT_UNIT_OPTIONS)[number];
export type UnitOption = WeightUnitOption | HeightUnitOption;

/** Перечень набора единиц у типа блока (рост — свой, иначе — вес). */
export function unitOptionsOf(
  type: string,
): typeof WEIGHT_UNIT_OPTIONS | typeof HEIGHT_UNIT_OPTIONS {
  return type === 'height_picker' ? HEIGHT_UNIT_OPTIONS : WEIGHT_UNIT_OPTIONS;
}

/**
 * ОДНА ДВЕРЬ набора единиц: какие единицы у выбора. Одна — только её
 * (переключатель одной плашкой, ответ в ней, `unitDefault` не действует);
 * нет поля, «both» или чужое значение — обе, в прежнем порядке. Зовут
 * отрисовка, единица ответа (`resolvePickerUnitDefault`) и панель.
 */
export function pickerUnits(
  type: string,
  raw: unknown,
): readonly [string] | readonly [string, string] {
  const [, first, second] = unitOptionsOf(type);
  if (raw === first || raw === second) return [raw === first ? first : second];
  return [first, second];
}

/** Кегль крупной цифры, когда автор его не задал, px. */
export const UNIT_PICKER_VALUE_FONT_SIZE_DEFAULT = 56;

export type UnitPickerLook = (typeof UNIT_PICKER_LOOKS)[number];
export type UnitToggleLook = (typeof UNIT_TOGGLE_LOOKS)[number];
export type UnitTogglePosition = (typeof UNIT_TOGGLE_POSITIONS)[number];
export type UnitPickerColorField = (typeof UNIT_PICKER_COLOR_FIELDS)[number];
export type UnitToggleTextTransform =
  (typeof UNIT_TOGGLE_TEXT_TRANSFORMS)[number];

/** Поля вида, которые читает отрисовка (те же имена, что в типах блока). */
export interface UnitPickerLookFields {
  unitPickerLook?: UnitPickerLook;
  unitPickerValueFontSize?: number;
  unitPickerValueColor?: string;
  unitPickerUnderline?: boolean;
  unitPickerUnderlineColor?: string;
  unitToggleLook?: UnitToggleLook;
  unitTogglePosition?: UnitTogglePosition;
  unitToggleTextTransform?: UnitToggleTextTransform;
  unitToggleFontSize?: number;
  unitToggleActiveBg?: string;
  unitToggleActiveText?: string;
  unitToggleBg?: string;
  unitToggleText?: string;
}

/** Разобранный вид: перечни — только свои значения, цвета — только коды. */
export interface ResolvedUnitPickerLook {
  look: UnitPickerLook;
  valueFontSize: number;
  valueColor?: string;
  underline: boolean;
  underlineColor?: string;
  toggleLook: UnitToggleLook;
  togglePosition: UnitTogglePosition;
  /** Только когда автор задал: без поля ключа нет (вид как раньше). */
  toggleTextTransform?: UnitToggleTextTransform;
  toggleFontSize?: number;
  toggleActiveBg?: string;
  toggleActiveText?: string;
  toggleBg?: string;
  toggleText?: string;
}

/** Тот же код цвета, что у сервера (BLOCK_COLOR_PATTERN): #rgb, #rrggbb, #rrggbbaa. */
const COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function colorOf(raw: unknown): string | undefined {
  return typeof raw === 'string' && COLOR.test(raw.trim())
    ? raw.trim()
    : undefined;
}

function oneOf<T extends string>(
  list: readonly T[],
  raw: unknown,
  fallback: T,
): T {
  return (list as readonly unknown[]).includes(raw) ? (raw as T) : fallback;
}

function fontSizeOf(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return UNIT_PICKER_VALUE_FONT_SIZE_DEFAULT;
  }
  const { min, max } = UNIT_PICKER_VALUE_FONT_SIZE_BOUNDS;
  return Math.min(max, Math.max(min, Math.round(raw)));
}

/** Пустые ключи не кладутся: сравнение «без полей — прежнее» остаётся точным. */
function withColor(
  target: ResolvedUnitPickerLook,
  key: keyof ResolvedUnitPickerLook,
  raw: unknown,
): void {
  const c = colorOf(raw);
  if (c) (target as unknown as Record<string, unknown>)[key] = c;
}

export function resolveUnitPickerLook(
  fields: UnitPickerLookFields,
): ResolvedUnitPickerLook {
  const r: ResolvedUnitPickerLook = {
    look: oneOf(UNIT_PICKER_LOOKS, fields.unitPickerLook, 'field'),
    valueFontSize: fontSizeOf(fields.unitPickerValueFontSize),
    underline: fields.unitPickerUnderline === true,
    toggleLook: oneOf(UNIT_TOGGLE_LOOKS, fields.unitToggleLook, 'buttons'),
    togglePosition: oneOf(
      UNIT_TOGGLE_POSITIONS,
      fields.unitTogglePosition,
      'right',
    ),
  };
  if (
    (UNIT_TOGGLE_TEXT_TRANSFORMS as readonly unknown[]).includes(
      fields.unitToggleTextTransform,
    )
  ) {
    r.toggleTextTransform = fields.unitToggleTextTransform;
  }
  const toggleSize = fields.unitToggleFontSize;
  if (typeof toggleSize === 'number' && Number.isFinite(toggleSize)) {
    const { min, max } = UNIT_TOGGLE_FONT_SIZE_BOUNDS;
    r.toggleFontSize = Math.min(max, Math.max(min, Math.round(toggleSize)));
  }
  withColor(r, 'valueColor', fields.unitPickerValueColor);
  withColor(r, 'underlineColor', fields.unitPickerUnderlineColor);
  withColor(r, 'toggleActiveBg', fields.unitToggleActiveBg);
  withColor(r, 'toggleActiveText', fields.unitToggleActiveText);
  withColor(r, 'toggleBg', fields.unitToggleBg);
  withColor(r, 'toggleText', fields.unitToggleText);
  return r;
}

/** Прежний кегль подписи переключателя: кнопки 14, пилюля 15. */
export function unitToggleBaseFontSize(toggleLook: UnitToggleLook): number {
  return toggleLook === 'pill' ? 15 : 14;
}

/**
 * ОДНА ДВЕРЬ подписи переключателя единиц (все четыре места отрисовки:
 * пара кнопок, одна плашка, две половины пилюли, одна половина): кегль —
 * заданный автором или прежний вида, регистр — только если автор его задал
 * (без поля ключа нет — разметка как раньше). Сторож концов считает вызовы.
 */
export function unitToggleLabelStyle(look: ResolvedUnitPickerLook): {
  fontSize: number;
  textTransform?: UnitToggleTextTransform;
} {
  const fontSize =
    look.toggleFontSize ?? unitToggleBaseFontSize(look.toggleLook);
  return look.toggleTextTransform
    ? { fontSize, textTransform: look.toggleTextTransform }
    : { fontSize };
}

/** Мелкая подпись единицы рядом с крупной цифрой — около 0,3 её кегля. */
export function unitSuffixFontSize(valueFontSize: number): number {
  return Math.round(valueFontSize * 0.3);
}

/**
 * Пара «футы + дюймы» → ПРЕЖНИЙ ответ пикера роста: голое число в футах
 * (как при вводе одним полем), два знака после точки. Ответ, проверка
 * границ и пересчёт ИМТ (`player/bodyMetrics.ts parseHeight`) не меняются.
 * Оба поля пустые — пустой ответ.
 */
export function feetInchesToValue(ft: string, inch: string): string {
  const f = ft.trim();
  const i = inch.trim();
  if (f === '' && i === '') return '';
  const feet = Number(f === '' ? 0 : f);
  const inches = Number(i === '' ? 0 : i);
  if (!Number.isFinite(feet) || !Number.isFinite(inches)) return '';
  return String(Number((feet + inches / 12).toFixed(2)));
}

/** Обратный разбор ответа в футах в пару полей; ноль дюймов — пустое поле. */
export function feetInchesOf(value: string): { ft: string; inch: string } {
  const text = value.trim();
  const n = text === '' ? NaN : Number(text);
  if (!Number.isFinite(n)) return { ft: '', inch: '' };
  let ft = Math.floor(n);
  let inch = Math.round((n - ft) * 12);
  if (inch === 12) {
    ft += 1;
    inch = 0;
  }
  return { ft: String(ft), inch: inch === 0 ? '' : String(inch) };
}
